import AddIcon from "@mui/icons-material/Add";
import { Button, Link, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import type { Promisable } from "type-fest";

import { DIRECTORY_PAGE_LIMIT_DEFAULT } from "@thalamos/haven-common";
import type { User, UserDirectoryPage, UserId } from "@thalamos/haven-common";

import { DirectoryFilter } from "./DirectoryFilter.js";
import { DirectoryTable } from "./DirectoryTable.js";

/**
 * A page of accounts filtered by name or email address, and the way to create
 * one where the audience may. Each audience supplies its own page of accounts
 * and where an account opens, so mounting it needs no API client.
 */
export const UsersListContent = ({
  page,
  loading,
  failed,
  filter,
  onFilter,
  offset,
  onPageChange,
  accountPath,
  onCreateUser,
}: {
  page: UserDirectoryPage | undefined;
  loading: boolean;
  failed: boolean;
  filter: string;
  onFilter: (query: string) => void;
  offset: number;
  onPageChange: (offset: number) => void;
  accountPath: (userId: UserId) => string;
  /** Without one, the page offers no way to create an account. */
  onCreateUser?: (() => Promisable<void>) | undefined;
}) => {
  const { t } = useTranslation();

  // The name is the link rather than a separate action column: the row is the
  // account, so the account's name is what opens it.
  const columns = [
    {
      id: "name",
      labelKey: "admin.users.name",
      render: (u: User) => (
        <Link component={RouterLink} to={accountPath(u.id)}>
          {u.name}
        </Link>
      ),
    },
    {
      id: "email",
      labelKey: "admin.users.email",
      render: (u: User) => u.email,
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Typography variant="h5" component="h1">
          {t("admin.users.title")}
        </Typography>
        {onCreateUser === undefined ? null : (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              void onCreateUser();
            }}
          >
            {t("admin.users.create.open")}
          </Button>
        )}
      </Stack>

      <DirectoryFilter
        value={filter}
        label={t("admin.users.filter")}
        onChange={onFilter}
      />

      <DirectoryTable
        rows={page?.results ?? []}
        total={page?.total ?? 0}
        loading={loading}
        error={failed}
        columns={columns}
        rowKey={(user) => user.id}
        limit={DIRECTORY_PAGE_LIMIT_DEFAULT}
        offset={offset}
        onPageChange={onPageChange}
      />
    </Stack>
  );
};
